import { useEffect, useState } from 'react'
import { firestore } from '../../firebase'
import { SearchOutlined } from '@ant-design/icons'
import { Table, Input, Space, Button, Statistic, Row, Col, Card, Typography, Divider, Skeleton } from 'antd'
import { CSVLink } from 'react-csv'
import Highlighter from 'react-highlight-words'
import moment from 'moment'
import DateRangeSelector from '../dateRangeSelector'
import { affLookup, sourceNameLookup } from '../../helpers/sources'

const { Title } = Typography

const TableLeadConversions = () => {

  const [isFetching, setIsFetching] = useState(false)
  const [groupedPartners, setGroupedPartners] = useState([])
  const [historyTable, setHistoryTable] = useState([])
  const [searchText, setSearchText] = useState(null)
  const [searchedColumn, setSearchedColumn] = useState(null)
  const [fetchStartDate, setFetchStartDate] = useState(null)
  const [fetchEndDate, setFetchEndDate] = useState(null)

  useEffect(() => {

    if (fetchStartDate && fetchEndDate) fetchHistory()

  }, [fetchStartDate, fetchEndDate])

  const fetchHistory = async () => {

    setIsFetching(true)

    try {

      // list of conversions
      const res = await firestore.collection('leads')
        .where('purchase_date', '>=', new Date(fetchStartDate.startOf('d').valueOf()))
        .where('purchase_date', '<=', new Date(fetchEndDate.endOf('d').valueOf()))
        .where('purchased', '==', true)
        .orderBy('purchase_date', 'desc')
        .get()

      if (res.empty) return

      // --- ALL ---
      const tableData = res.docs.map((x, i) => {

        const startDate = moment(x.data().created_at.toDate())
        const endDate = moment(x.data().purchase_date.toDate())

        const diffDuration = moment.duration(endDate.diff(startDate));

        const diffDays = diffDuration.days();
        const diffHours = diffDuration.hours();
        const diffMinutes = diffDuration.minutes();

        let delta = ''
        delta = diffDays > 0 ? delta += `${diffDays} days` : delta
        delta = diffHours > 0 ? delta += ` ${diffHours} hours` : delta
        delta = diffMinutes > 0 ? delta += ` ${diffMinutes} minutes` : delta

        return {
          ...x.data(),
          key: i,
          email: x.data().email,
          source_name: sourceNameLookup(x.data()),
          original_source: affLookup.find(source => source.value === Number(x.data().original_affId))?.key || '',
          offer: x.data().offer.includes('@') ? 'n/a' : x.data().offer,
          delta
        }
      })

      const uniqueEmailsSet = new Set();
      const deduplicatedArray = tableData.filter(obj => {
        if (!uniqueEmailsSet.has(obj.email)) {
          uniqueEmailsSet.add(obj.email)
          return true
        }
        return false
      })

      // --- GROUPED ---
      const grouped = deduplicatedArray
        .reduce((acc, obj) => {

          const category = obj.source_name
          const existingArray = acc.find((arr) => arr[0]?.source_name === category)
        
          if (existingArray) {
            existingArray.push(obj)
          } else {
            acc.push([obj])
          }
        
          return acc
        }, [])

      setGroupedPartners(grouped)
      setHistoryTable(deduplicatedArray)

    } catch (error) {

      console.error({error})

    } finally {

      // remove loading
      setIsFetching(false)
    }
  }

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => { searchInput = node }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({ closeDropdown: false })
              setSearchText(selectedKeys[0])
              setSearchedColumn(dataIndex)
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    render: text =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = clearFilters => {
    clearFilters()
    setSearchText('')
  }

  const columns = [
    {
      width: 150,
      key: 'created_at',
      title: 'Subscribed on',
      dataIndex: 'created_at',
      sorter: (a, b) => a.created_at - b.created_at,
      render: text => `${moment(text.toDate()).format('MM/DD/YY hh:mm A')}`
    }, {
      width: 150,
      key: 'purchase_date',
      title: 'Purchased on',
      dataIndex: 'purchase_date',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.purchase_date - b.purchase_date,
      render: text => `${moment(text.toDate()).format('MM/DD/YY hh:mm A')}`
    }, {
      width: 250,
      key: 'delta',
      title: 'Delta',
      dataIndex: 'delta'
    }, {
      width: 225,
      key: 'email',
      title: 'Email',
      dataIndex: 'email'
    }, {
      width: 150,
      key: 'source_name',
      title: 'Source',
      dataIndex: 'source_name',
      ...getColumnSearchProps('source_name')
    }, {
      width: 150,
      key: 'original_source',
      title: 'Last Source',
      dataIndex: 'original_source',
      ...getColumnSearchProps('original_source')
    }, {
      width: 100,
      key: 'offer',
      title: 'Offer',
      dataIndex: 'offer',
      ...getColumnSearchProps('offer')
    }
  ]

  return (
    <>
      <Title level={2}>Leads Converted</Title>

      <Row gutter={[16, 16]} justify='end' align='center'>
        <Col sm={8} md={4} >
          <Button
            type='primary'
            loading={isFetching}
            onClick={fetchHistory}
          >
            Refresh Data
          </Button>
        </Col>
      </Row>

      {(isFetching) &&
        <Skeleton active={true} paragraph={{ rows: 1 }} style={{ marginTop: '-1.25rem' }} />
      }

      {!(isFetching) &&
        <Row gutter={[16, 16]} justify='space-between' align='middle'>
          <Col xm={24} sm={16} style={{ display: 'flex', flexDirection: 'column' }}>
            <DateRangeSelector fetchStartDate={fetchStartDate} fetchEndDate={fetchEndDate} onChange={({ startDate, endDate }) => {
              setHistoryTable([])
              setGroupedPartners([])
              setFetchStartDate(startDate)
              setFetchEndDate(endDate)
            }} />
          </Col>
        </Row>
      }

      {historyTable.length > 0 &&
        <Row gutter={[16, 16]}>
          <Card variant='borderless'>
            <Statistic title='Records' value={historyTable.length} />
          </Card>
        </Row>
      }

      {groupedPartners.length > 0 &&
        <>
          <Divider orientation='left'>Sources</Divider>
          <Row gutter={[16, 16]}>
            {groupedPartners.sort((a,b) => b.length - a.length).map((x, i) =>
              <Col key={i} span={4}>
                <Card size='small' title={`${x[0].source_name}`}>
                  <Statistic value={x.length} />
                </Card>
              </Col>
            )}
          </Row>
        </>
      }

      <Row justify='space-between' align='middle'>
        <Table
          style={{ paddingTop: 20 }}
          onChange={(pagination, filters, sorter, extra) => setHistoryTable(extra.currentDataSource)}
          loading={isFetching}
          columns={columns}
          dataSource={historyTable}
          scroll={{ x: 1000, y: 500 }}
          size='small'
          title={() => (
            <CSVLink
              filename={'BTM_Sales_Page_Transactions.csv'}
              data={historyTable}
              className='btn btn-primary'
            >
              Export to CSV
            </CSVLink>
          )}
        />
      </Row>
    </>
  )
}

export default TableLeadConversions